import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import moment from 'moment-timezone';

// components
import { FormRow } from '../components/layout';
import { ReadyForCallButton } from '../components';
import { Collapse, LoaderButton } from '../../../common/components';

// utils
import { prettifyPhone } from '../../../common/utils/prettify';
import { getParam } from '../../../common/utils/qs';
import { sendSMS } from '../../../actions/telephony';

import './styles.scss';

// eslint-disable-next-line
function TextInput({
  name,
  label,
  inputRef,
  className,
  value,
  disabled,
  isValid, // eslint-disable-line
}) {
  const dispatch = useDispatch();
  const val = useSelector(store => store.values[name]);

  function handleChange(e) {
    const v = e.target.value;
    dispatch({ type: 'SET_FIELD_VALUE', name, value: v });
  }

  let isValidClass = '';
  if (isValid === true) isValidClass = 'is-valid';
  if (isValid === false) isValidClass = 'is-invalid';

  return (
    <div className={className}>
      <label htmlFor={name} className="form-label">{label}</label>
      <input
        ref={inputRef}
        className={`form-control ${isValidClass}`}
        name={name}
        id={name}
        value={(value !== undefined) ? value : val}
        onChange={handleChange}
        disabled={disabled}
      />
    </div>
  );
}

function Checkbox({ name, label, className }) {
  const dispatch = useDispatch();
  const value = useSelector(store => store.values[name]);

  function handleChange(e) {
    const v = e.target.checked;
    dispatch({ type: 'SET_FIELD_VALUE', name, value: v });
  }

  return (
    <div className={`form-check ${className}`}>
      <input
        id={`checkbox-${name}`}
        className="form-check-input"
        type="checkbox"
        onChange={handleChange}
        checked={value}
      />
      <label className="form-check-label" htmlFor={`checkbox-${name}`}>
        {label}
      </label>
    </div>
  );
}

function SMSCallLink() {
  const dispatch = useDispatch();
  const {
    sms_call_same: smsCallSame,
    sms_last_sent: smsLastSent,
    shopper_phone_sms: shopperPhoneSms,
    shopper_phone: shopperPhone,
  } = useSelector(store => store.values);

  const [ loading, setLoading ] = useState(false);
  const [ error, setError ] = useState(false);
  const [ touched, setTouched ] = useState(false);

  const phone = smsCallSame ? shopperPhone : shopperPhoneSms;

  function handleSubmit() {
    setLoading(true);
    setError(false);

    sendSMS(phone)
      .then(() => {
        const sentTime = moment.tz(moment.now(), moment.tz.guess()).toISOString();
        dispatch({ type: 'SET_FIELD_VALUES', values: { sms_last_sent: sentTime } });
        setLoading(false);
      }).catch((err) => {
        setLoading(false);
        setError(true);
        console.log('[Send SMS error]');
        console.log(err);
      });
  }

  const re = /^[0-9]{10}$/g;
  const isPhoneValid = re.test(phone);

  return (
    <div className="mt-3">
      <Checkbox
        label="Phone # for SMS is the same phone # for calls"
        name="sms_call_same"
        className="mb-0"
      />
      <TextInput
        label="Client phone number (SMS)"
        name="shopper_phone_sms"
        disabled={smsCallSame}
        value={smsCallSame ? shopperPhone : undefined}
        className="mt-3"
        isValid={touched ? isPhoneValid : undefined}
        onBlur={() => setTouched(true)}
      />
      <div className="col">
        {smsLastSent && !error && (
          <i style={{ color: 'var(--bs-info)' }}>
            <b>{`Link successfully sent at ${moment(smsLastSent).format('MM/DD/YYYY h:mma')}`}</b>
          </i>
        )}
        { error && (<i style={{ color: 'var(--bs-danger)' }}><b>Error sending SMS</b></i>) }
      </div>
      <LoaderButton
        type="button"
        className="btn btn-outline-secondary me-3 my-3"
        disabled={!isPhoneValid}
        onClick={() => handleSubmit()}
        text="Send call link via SMS"
        loadingText="Sending"
        loading={loading}
      />
    </div>
  );
}

function Call() {
  const {
    telephony: { status },
    enrollment: { enrollmentCode },
    agent: {
      phoneInfo = {},
      mobilePhone: agentPhone,
    },
    values: {
      shopper_can_receive_sms: shopperCanReceiveSMS,
    },
  } = useSelector(store => store);
  const { phone: enrollmentPhone = '' } = phoneInfo;

  // if the querystring focusPhone is set then autofocus the phone input
  const location = useLocation();
  const ref = useRef();
  useEffect(() => {
    const focusPhone = getParam('focusPhone');
    if (ref.current && focusPhone) ref.current.focus();
  }, [location]);

  const loggedIn = (status !== 'noauth');

  return (
    <div className="row">
      <div className="col-md-6">
        <FormRow>
          <b>Note for agent</b>
        </FormRow>

        <FormRow>
          <em>
            Your client must be the one to initiate the enrollment call.
            Once they have done so, you will be able to call back if the call drops.
          </em>
        </FormRow>

        <FormRow>
          <em>
            <ol>
              <li>
                Make sure to click the &apos;Ready to receive call from client&apos; checkbox
                (you cannot receive a call until you click this). Please be patient,
                it may take up to 30 seconds to login to the telephony system.
              </li>
              <li>
                Enter your client&apos;s phone number. This will enable you to call back if the call drops.
              </li>
              <li>
                Give your client the telephonic enrollment number to call and the enrollment code to enter,
                OR if they are able to receive texts, simply click &apos;Send call link via SMS.&apos;
                From there, your client will be able to initiate the call.
              </li>
            </ol>
          </em>
        </FormRow>
      </div>
      <div className="col-md-6">
        <div className="d-flex mb-4">
          <ReadyForCallButton />
        </div>

        <div className="phone-box">
          <div className="d-flex justify-content-between">
            <span>Telephonic enrollment number:</span>
            <span className="fw-bold ms-2">{loggedIn ? prettifyPhone(enrollmentPhone) : '###-###-####'}</span>
          </div>
          <div className="d-flex justify-content-between">
            Enrollment code:
            <b className="ms-2">{loggedIn ? enrollmentCode : '######'}</b>
          </div>
          <div className="d-flex justify-content-between">
            Direct phone number:
            <b className="ms-2">{loggedIn ? prettifyPhone(agentPhone) : '###-###-####'}</b>
          </div>
        </div>

        <div className="d-flex mt-4">
          <TextInput
            inputRef={ref}
            label="Client phone number"
            name="shopper_phone"
          />
        </div>
        <div className="d-flex mt-4 mb-3">
          <Checkbox
            label="Client can receive SMS"
            name="shopper_can_receive_sms"
          />
        </div>
        <Collapse isOpen={shopperCanReceiveSMS} indent>
          <SMSCallLink />
        </Collapse>

      </div>
    </div>
  );
}

export default Call;
